import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import Swal from 'sweetalert2';

function Variationreport() {
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [variationData, setVariationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) setUserId(user.uid);
    });
    return () => unsub();
  }, []);

  const fetchVariationData = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      // Assuming 'daily_stock' collection contains: 
      // date, product (MS/HSD), opening, purchase, sales, book_closing, actual_closing
      const q = query(
        collection(db, "daily_stock"),
        where("userId", "==", userId),
        where("date", ">=", fromDate),
        where("date", "<=", toDate),
        orderBy("date", "desc")
      );

      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => {
        const item = doc.data();
        const variation = (item.actual_closing || 0) - (item.book_closing || 0);
        return { id: doc.id, ...item, variation };
      });
      setVariationData(data);
    } catch (error) {
      console.error("Error fetching variation:", error);
      Swal.fire('Error', 'Failed to fetch stock data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="content-wrapper bg-light min-vh-100">
      {/* FILTER SECTION */}
      <section className="content-header no-print p-4">
        <div className="card shadow-sm border-0 p-3">
          <div className="row align-items-end">
            <div className="col-md-3">
              <label className="small font-weight-bold text-muted">From Date</label>
              <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="small font-weight-bold text-muted">To Date</label>
              <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
            <div className="col-md-4">
              <button className="btn btn-navy px-4 mr-2 shadow-sm" onClick={fetchVariationData} disabled={loading}>
                {loading ? 'Processing...' : 'View Variation'}
              </button>
              <button className="btn btn-outline-secondary shadow-sm" onClick={handlePrint}>
                <i className="fas fa-print mr-1"></i> Print
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* REPORT DISPLAY */}
      <section className="content px-4 py-2 report-print-area">
        <div className="card border-0 shadow-lg overflow-hidden">
          <div className="card-header bg-navy text-white text-center py-3">
            <h4 className="mb-0 font-weight-bold">Stock Variation Report (MS / HSD)</h4>
            <small>Period: {fromDate} to {toDate}</small>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0 var-table">
                <thead className="bg-light text-center small text-uppercase font-weight-bold">
                  <tr>
                    <th>Date</th>
                    <th>Product</th>
                    <th>Opening</th>
                    <th>Purchase</th>
                    <th>Sales</th>
                    <th className="bg-soft-blue">Book Closing</th>
                    <th className="bg-soft-yellow">Actual (Dip)</th>
                    <th>Variation</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {variationData.length > 0 ? variationData.map((row) => (
                    <tr key={row.id} className="text-center">
                      <td className="font-weight-bold">{row.date}</td>
                      <td>
                        <span className={`badge ${row.product === 'MS' ? 'badge-warning' : 'badge-primary'}`}>
                          {row.product}
                        </span>
                      </td>
                      <td>{row.opening} Ltr</td>
                      <td className="text-success">+{row.purchase || 0}</td>
                      <td className="text-danger">-{row.sales || 0}</td>
                      <td className="font-weight-bold">{row.book_closing}</td>
                      <td className="font-weight-bold text-info">{row.actual_closing}</td>
                      <td className={`font-weight-bold ${row.variation < 0 ? 'text-danger' : 'text-success'}`}>
                        {row.variation > 0 ? `+${row.variation}` : row.variation}
                      </td>
                      <td>
                        {row.variation === 0 ? (
                          <span className="text-success small"><i className="fas fa-check-circle mr-1"></i>Matched</span>
                        ) : Math.abs(row.variation) > 10 ? (
                          <span className="text-danger small font-weight-bold"><i className="fas fa-exclamation-triangle mr-1"></i>High Var</span>
                        ) : (
                          <span className="text-warning small"><i className="fas fa-info-circle mr-1"></i>Minor</span>
                        )}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="9" className="text-center py-5 text-muted">Select dates and click "View Variation" to load records.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <style dangerouslySetInnerHTML={{ __html: `
        .btn-navy { background: #0f172a; color: white; border: none; }
        .btn-navy:hover { background: #1e293b; color: white; }
        .bg-navy { background: #0f172a !important; }
        .bg-soft-blue { background-color: #f1f5f9; }
        .bg-soft-yellow { background-color: #fffbeb; }
        .var-table td { vertical-align: middle; border-bottom: 1px solid #f1f5f9; }
        
        @media print {
          .no-print { display: none !important; }
          .content-wrapper { background: white !important; padding: 0 !important; margin: 0 !important; }
          .report-print-area { width: 100%; padding: 0 !important; }
          .card { box-shadow: none !important; border: 1px solid #ddd !important; }
          .table { font-size: 11px !important; }
          @page { size: landscape; margin: 1cm; }
        }
      `}} />
    </div>
  );
}

export default Variationreport;